import { useEffect, useRef, useState } from "react";
import type { FinalInfo } from "@studio/shared";
import { InlineError, LoadingPage, StudioShell, mvStudioStep } from "../components/StudioShell";
import {
  ApiError,
  apiRequest,
  downloadFinalVideo,
  getFinal,
  useWork,
} from "../lib/api";
import { navigate } from "../lib/navigation";
import { pathForStep } from "../lib/session";

function formatDuration(value: number): string {
  const seconds = Math.max(0, Math.round(value));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

export function FinalPage({ workId }: { workId: string }) {
  const work = useWork(workId);
  const [final, setFinal] = useState<FinalInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getFinal(workId).then((result) => {
      if (!cancelled) {
        setFinal(result);
        setMissing(false);
        setError(null);
      }
    }).catch((caught: unknown) => {
      if (cancelled) return;
      if (caught instanceof ApiError && caught.status === 404) {
        setMissing(true);
      } else {
        setError(caught instanceof ApiError ? caught.message : "완성 영상을 불러오지 못했어요.");
      }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [workId]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 2_500);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const backToEdit = async (): Promise<void> => {
    setError(null);
    try {
      await apiRequest(`/api/works/${encodeURIComponent(workId)}/autosave`, {
        method: "POST",
        body: JSON.stringify({ step: "edit", gifts_v: 2 }),
      });
      navigate(pathForStep(workId, "edit"));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "편집실로 돌아가지 못했어요.");
    }
  };

  if (loading || work.loading) return <LoadingPage label="완성 영상을 불러오는 중..." />;
  if (missing || !final) {
    return (
      <StudioShell currentStep={mvStudioStep.final} imageRegenRemaining={work.data?.image_regen_remaining}>
        <section className="final-missing-card">
          <span aria-hidden="true">↩</span>
          <h1 tabIndex={-1}>완성 영상을 찾지 못했어요</h1>
          <p>편집실에서 컷을 확인하고 영상을 다시 완성해 주세요.</p>
          {error ? <InlineError>{error}</InlineError> : null}
          <button className="button-primary" type="button" onClick={() => void backToEdit()}>편집실로 돌아가기</button>
        </section>
      </StudioShell>
    );
  }

  return (
    <StudioShell currentStep={mvStudioStep.final} imageRegenRemaining={work.data?.image_regen_remaining}>
      <div className="final-page">
        <section className="final-card">
          <div className="final-success-icon" aria-hidden="true">✓</div>
          <h1 tabIndex={-1}>뮤직비디오를 완성했어요</h1>
          <p className="final-meta">{final.title} · {formatDuration(final.duration)} · 720p</p>
          <div className="final-video-stage">
            <video ref={videoRef} src={final.video_url} controls preload="metadata" aria-label={`${final.title} 완성 영상`} />
          </div>
          <button
            className="button-secondary final-replay-button"
            type="button"
            onClick={() => {
              if (!videoRef.current) return;
              videoRef.current.currentTime = 0;
              void videoRef.current.play().catch(() => setError("재생 버튼을 다시 눌러 주세요."));
            }}
          >▶ 완성작 다시 보기</button>
          <div className={`final-actions${final.download_allowed ? "" : " single"}`}>
            {final.download_allowed ? (
              <button
                className="button-primary"
                type="button"
                onClick={() => {
                  setToast("저장했어요 ✓");
                  setError(null);
                  void downloadFinalVideo(workId, final.title).catch((caught: unknown) => {
                    setError(caught instanceof ApiError ? caught.message : "영상을 저장하지 못했어요.");
                  });
                }}
              >영상 저장 (MP4)</button>
            ) : null}
            <button className="button-secondary" type="button" onClick={() => void backToEdit()}>편집실로 돌아가기</button>
          </div>
          {!final.download_allowed ? (
            <p className="final-upload-note">올린 음악으로 만든 영상은 우리 반 갤러리·감상회에서 볼 수 있어요</p>
          ) : null}
          <p className="final-credit-note">영상 끝에 만든 사람과 음악 출처 크레딧이 자동으로 들어가요 (수업 목적 사용)</p>
          {error ? <InlineError>{error}</InlineError> : null}
        </section>
      </div>
      {toast ? <div className="toast" role="status">{toast}</div> : null}
    </StudioShell>
  );
}
